import { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);

export const SETUP_FEE_AMOUNT = 1500;
export const SETUP_FEE_LABEL = "Artwork & setup — one-time fee";
const normalizeArtwork = (i) => {
  const mode = i.artwork === "reorder" ? "reorder" : "new";
  return {
    ...i,
    artwork: mode,
    artwork_label: mode === "reorder" ? "Reorder Existing Artwork" : "New Artwork / First-Time Order",
    artwork_reference: mode === "reorder" ? i.artwork_reference || "" : "",
    artwork_amount: mode === "new" ? SETUP_FEE_AMOUNT : 0,
  };
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      // drop legacy gift-box / upgrade-only pricing from stored carts; server is authoritative anyway
      return JSON.parse(localStorage.getItem("es_cart") || "[]").filter((i) => i && i.lookup_key).map((i) => normalizeArtwork({
        ...i, gift_box: false,
        amount: i.gift_box && i.gift_box_amount ? i.amount - i.gift_box_amount : i.amount,
        upgrade: i.upgrade && i.lookup_key.startsWith("metal_business_cards") ? false : i.upgrade,
        upgrade_label: i.upgrade && i.lookup_key.startsWith("metal_business_cards") ? "" : i.upgrade_label,
      }));
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("es_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, { quantity = 1, engraving = "", notes = "", design_file_id = "", design_filename = "", colour = false, colour_amount = 0, lookup_key = "", base_amount = 0, size = "", yeti = false, yeti_amount = 0, yeti_label = "", upgrade = false, upgrade_amount = 0, upgrade_label = "", artwork = "new", artwork_reference = "", coat = "", font = "", image = "", pack_size = 0 } = {}) => {
    setItems((prev) => {
      const lk = lookup_key || product.lookup_key;
      const base = base_amount || product.amount;
      const key = `${lk}::${engraving}::${notes}::${design_file_id}::${colour ? "c" : ""}::${yeti ? "y" : ""}::${upgrade ? "u" : ""}::${artwork}::${artwork_reference}::${coat}::${font}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + quantity } : i));
      }
      return [...prev, normalizeArtwork({
        key,
        lookup_key: lk,
        slug: product.slug,
        name: product.name + (size ? ` · ${size}` : ""),
        amount: base + (colour ? colour_amount : 0) + (yeti ? yeti_amount : 0) + (upgrade ? upgrade_amount : 0),
        image: image || product.image,
        quantity,
        engraving,
        notes,
        design_file_id,
        design_filename,
        colour,
        yeti,
        yeti_label,
        upgrade,
        upgrade_label,
        artwork,
        artwork_reference,
        pack_size,
        coat,
        font,
      })];
    });
    setOpen(true);
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const setQuantity = (key, quantity) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity: Math.min(999, Math.max(1, quantity)) } : i)));
  const clearCart = () => setItems([]);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const hasNewArtwork = items.some((i) => i.artwork === "new");
  const setupFee = hasNewArtwork ? SETUP_FEE_AMOUNT : 0;
  const total = items.reduce((s, i) => s + i.amount * i.quantity, 0) + setupFee;

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, setQuantity, clearCart, count, total, setupFee, hasNewArtwork, open, setOpen }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
